import React from 'react';
import { noop, map, isEmpty, get, fromPairs, isEqual } from 'lodash';
import classnames from 'classnames';

// local dependencies
import { button as Button } from 'modules/button';
import { getLabel } from 'utils/getLabel';
import { Icon } from 'modules/icon-bank';
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { widgetError as WidgetError } from 'ui/views/widgets/widgetError';
import { WIDGET_ERROR_TYPE } from 'constants/common';
import { Form } from 'modules/form';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { cSrOrderNotes as CSrOrderNotes } from 'ui/views/widgets/cSROrderNotes';
 *
 * // render
 * <CSrOrderNotes propName='propValue'/>
 */

const ViewNotes = ( props ) => {
    return (
        <>
         {  isEmpty( props.items ) ?
                <WidgetError type={ WIDGET_ERROR_TYPE.NO_DATA_FOUND} />
                :
            <div className='ui-csr-order-notes__body__wrap'>
                { map( props.items, ( item, itemIndex ) => (
                    <div key={ item.id + itemIndex } className='ui-csr-order-notes__body__table'>
                        <div className='ui-csr-order-notes__body__title'>
                            { getLabel( props.data.displayDateText ) } : { item.noteDate }
                        </div>
                        <div className='ui-csr-order-notes__body__description'>
                            {item.comment}
                        </div>
                    </div>
                ) )}
            </div>
        }
            {/* button actions */}
            <div className='ui-csr-order-notes__body__actions'>
                { map( props.data.actions, action => {
                    return (
                        <Button
                            key={ action.name }
                            { ...action }
                            outline={ 'cancel' !== action.name ? true : false }
                            size='large'
                            className='ui-csr-order-notes__body__actions__action'
                            onClick={ () => 'cancel' !== action.name ? props.onClickAdd( true ) : props.onCancelClick() }
                        />
                    );
                } )}
            </div>
        </>
    );
};

/******************************************************** */

const AddNotes = ( props ) => {

    const formActions = get( props.data, 'formActions', [] );
    const formFields = get( props.data, 'fields', '' );
            
    return (
        <Form
            fields={ fromPairs( map( formFields, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
            isValid={false}
            mode={props.mode}
            onSubmit={( { values, isValid } ) => {
                if ( isValid ) {
                    props.onSubmitClick( {
                        ...values
                    } );
                }
            }}
        >
            {( { fields, values, isValid, errors, onChange, onSumbit } = {} ) => {

                return (
                    <form
                        onSubmit={onSumbit}
                        className='ui-csr-order-notes__body__form'
                    >
                        { /* fields */}
                        { map( fields, ( field ) => {

                            const fieldClassName = classnames( 'ui-csr-order-notes__body__form__field',
                                `ui-csr-order-notes__body__form__field--${field.name}`
                            );

                            return (
                                <field.view
                                    className= { fieldClassName }
                                    key={ field.name }
                                    name={ field.name }
                                    required={ field.required }
                                    placeholder={ field.placeholder }
                                    label={ field.title }
                                    onChange={ onChange }
                                    value={ values[ field.name ] }
                                    error={ errors[ field.name ] }
                                    maxLength={ field.maxLength }
                                    options={ props.data.options[ field.options ] }
                                    requestPayload={ props.requestPayload }
                                />
                            );
                        } )}

                        {/* button actions */}
                        <div className='ui-csr-order-notes__body__form__actions'>
                            { map( formActions, action => {
                                return (
                                    <Button
                                        key={action.name}
                                        id={action.name}
                                        type={ 'cancel' !== action.name ? 'submit' : 'button' }
                                        disabled={'cancel' !== action.name ? !isValid : false }
                                        {...action}
                                        outline={ 'cancel' === action.name ? true : false }
                                        size='large'
                                        className='ui-csr-order-notes__body__form__actions__action'
                                        onClick={ isEqual( 'cancel', action.name ) ? () => {

                                            // props.onCancelClick();
                                            
                                            props.onClickAdd( false );
                                        } : null }
                                    />
                                );
                            } ) }
                        </div>
                    </form>
                );
            }}
        </Form>
    );
};

/**************************************** */

export const cSrOrderNotesSuccess = ( props ) => {
    
    return (
        <div className='ui-csr-order-notes'>
               
            <div className='ui-csr-order-notes__header'>
                {/* title for the modal */}
                <div className='ui-csr-order-notes__header__title'>
                    { getLabel( props.data.title ) }
                </div>

                {/* close button */}
                <div className='ui-error-overlay__head__close' onClick={ () => props.closeOverlay() }>
                    <Icon name='close' width='14' height='14' fill='#ffffff' />
                </div>
            </div>

            { ! props.enableAdd ? (
                <div className='ui-csr-order-notes__body'>
                    <ViewNotes { ...props } />
                </div>
            ) : (
                <div className='ui-csr-order-notes__body'>
                    <AddNotes  { ...props } />
                </div>
            )   }
              
        </div>
    );
};


// set display name
cSrOrderNotesSuccess.displayName = 'cSrOrderNotesSuccess';


// set default props
cSrOrderNotesSuccess.defaultProps = {
    componentData: {},
    layoutConfig: {},
    onActionClick: noop,
};

// prop types
cSrOrderNotesSuccess.propTypes = {};

/*******************************/

// preloader view
export const cSrOrderNotesPreloader = ( props ) => {

    // widget class name
    const widgetClassName = classnames( 'ui-csr-order-notes-preloader', props.className );

    return (
        <div className={widgetClassName}>
            loading...
        </div>
    );
};

// set display name
cSrOrderNotesPreloader.displayName = 'cSrOrderNotesPreloader';

// set default props
cSrOrderNotesPreloader.defaultProps = {};

// prop types
cSrOrderNotesPreloader.propTypes = {};

/*******************************/

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { cSrOrderNotes as cSrOrderNotes } from 'ui/views/widgets/cSrOrderNotes';
 *
 * // render
 * <cSrOrderNotes propName='propValue'/>
 */
export const cSrOrderNotes = withNetworkState( cSrOrderNotesPreloader, cSrOrderNotesSuccess );
